/**
 * Route layer — draws every route as a line with waypoint markers and per-leg
 * labels (bearing + distance). The active route is highlighted.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';
import {
  describeLeg,
  monitorRoute,
  clampFollowLegIndex,
  APPROACH_LEG_INDEX,
  summariseRoute,
  effectiveLegXtd,
  effectiveWaypointRadius,
  type Route,
} from '@/routing';
import { rhumbBearingDeg, rhumbDestination } from '@/nav';
import { circleRing } from '@/safety';
import { getPalette } from './style/palettes';
import type { Palette } from '@/store';

const SOURCE_ID = 'routes';
const LABEL_SOURCE_ID = 'route-labels';

/**
 * The route layer ids, bottom -> top in draw order. Also the order we re-seat them
 * in so they always end up above the chart cells.
 */
const ROUTE_LAYER_IDS = [
  'route-xtd-run',
  'route-xtd-band',
  'route-xtd-edge',
  'route-wp-ring',
  'route-line',
  'route-hazard-leg',
  'route-active-leg-seg',
  'route-active-leg',
  'route-waypoints',
  'route-xtd-handle',
  'route-waypoint-labels',
  'route-leg-labels',
] as const;

/**
 * Lift the route layers to the top of the stack, in draw order, so the route line
 * and its waypoints are never buried. Same reason as the ownship marker: the
 * training/vector chart cells load asynchronously, so if a placed waypoint or a
 * played route creates these layers before the cells finish loading, addCellToMap
 * appends the cell layers ABOVE them. On a raster provider the cells are hidden so
 * it looked fine; on the vector provider the opaque depth/land fills cover the
 * waypoints. moveLayer with no `before` moves a layer to the top, so walking
 * bottom -> top leaves the draw order intact. Missing layers are skipped.
 */
export function raiseRouteLayers(map: MaplibreMap): void {
  for (const id of ROUTE_LAYER_IDS) {
    if (map.getLayer(id)) map.moveLayer(id);
  }
}

/** When following, the boat position used to draw the active-leg line. */
export interface FollowInfo {
  routeId: string;
  boat: [number, number];
  /** When following from a chosen waypoint, the leg index to start at. */
  fromLegIndex?: number;
  /**
   * The authoritative active leg (0-based) once a route is activated for follow.
   * When set it decides which leg is drawn as the active one; when absent we fall
   * back to the nearest-leg pick so an older follow still highlights a leg.
   */
  legIndex?: number | null;
}

/** The corridor's colour: safe water reads green, on every palette. */
const XTD_GREEN = '#22a06b';

/**
 * Nautical miles of half-corridor to zoom-0 pixels of FULL width at this
 * latitude. The 1852 is the fix for fb-msn7t0xn-1: the first cut divided the
 * XTD as if it were metres when it arrives in nautical miles, which made a
 * 0.2 nm corridor about a twentieth of a pixel wide — drawn, technically.
 */
export function xtdRunW0(xtdNm: number, latDeg: number): number {
  return (2 * xtdNm * 1852) / (156543.03392 * Math.cos((latDeg * Math.PI) / 180));
}

/** The leg whose XTD corridor is being edited (drawn prominent, with handles). */
export interface EditXtd {
  routeId: string;
  fromId: string;
}

/**
 * The result of a "Check route" run, reduced to what the chart needs: which legs
 * of which route to hatch, and how each reads. Four distinct severities so a
 * shallow leg no longer looks the same whatever the tide does:
 *   - 'hazard'  a confirmed problem: crosses land, or too shallow even at HW.
 *   - 'gate'    shallow but passable within a tidal gate (mind the timing).
 *   - 'manual'  shallow with no tide-height data, needs a manual tide calc.
 *   - 'caution' softer: no depth data, or outside loaded coverage.
 * Drawn under the magenta active-leg casing, so a followed leg's highlight is
 * never hidden.
 */
export type RouteHazardSeverity = 'hazard' | 'gate' | 'manual' | 'caution';
export interface RouteHazardHighlight {
  routeId: string;
  legs: { legIndex: number; severity: RouteHazardSeverity }[];
}

/**
 * On-chart label for a waypoint: its own name, or the 1-based sequence number
 * when it hasn't got one. So a freshly dropped or GPX-imported point still reads
 * as "1", "2", "3"… rather than showing nothing until we name it.
 */
function waypointLabel(name: string | undefined, index: number): string {
  const n = name?.trim();
  return n ? n : String(index + 1);
}

/**
 * Build the route feature + label collections. Exported so a test can check what
 * ends up on the chart (e.g. that a route's own colour rides through onto its
 * line feature) without standing up a real map.
 */
export function buildRouteFeatures(
  routes: Route[],
  activeRouteId: string | null,
  follow?: FollowInfo | null,
  editXtd?: EditXtd | null,
  hazards?: RouteHazardHighlight | null,
): {
  features: FeatureCollection;
  labels: FeatureCollection;
} {
  const features: GeoJSON.Feature[] = [];
  const labels: GeoJSON.Feature[] = [];

  // Warning hatch over any leg the route check flagged, drawn as its own line
  // features so the paint can colour by severity. Sits below the active-leg
  // magenta (see ROUTE_LAYER_IDS order), so a followed leg still reads clearly.
  if (hazards && hazards.legs.length) {
    const r = routes.find((rr) => rr.id === hazards.routeId);
    if (r) {
      for (const { legIndex, severity } of hazards.legs) {
        const a = r.waypoints[legIndex]?.position;
        const b = r.waypoints[legIndex + 1]?.position;
        if (!a || !b) continue; // leg no longer exists (route edited since the check)
        features.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [a, b] },
          properties: { kind: 'hazard-leg', routeId: r.id, legIndex, severity },
        });
      }
    }
  }

  // XTD corridors + waypoint arrival rings, per route.
  for (const r of routes) {
    for (const w of r.waypoints) {
      const rad = effectiveWaypointRadius(r, w);
      if (rad != null && rad > 0) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [circleRing(w.position, rad)] },
          properties: {
            kind: 'wp-ring',
            routeId: r.id,
            wpId: w.id,
            cLon: w.position[0],
            cLat: w.position[1],
          },
        });
      }
    }
    for (let i = 0; i < r.waypoints.length - 1; i++) {
      const a = r.waypoints[i].position;
      const b = r.waypoints[i + 1].position;
      const fromId = r.waypoints[i].id;
      const xtd = effectiveLegXtd(r, fromId);
      if (xtd == null || xtd <= 0) continue;
      const brg = rhumbBearingDeg(a, b);
      const aL = rhumbDestination(a, brg - 90, xtd);
      const bL = rhumbDestination(b, brg - 90, xtd);
      const aR = rhumbDestination(a, brg + 90, xtd);
      const bR = rhumbDestination(b, brg + 90, xtd);
      const editing = !!editXtd && editXtd.routeId === r.id && editXtd.fromId === fromId;
      features.push({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [[aL, bL, bR, aR, aL]] },
        properties: { kind: 'xtd-band', editing, routeId: r.id, fromId },
      });
      if (editing) {
        const mid: [number, number] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
        for (const side of [-90, 90]) {
          features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: rhumbDestination(mid, brg + side, xtd) },
            properties: { kind: 'xtd-handle', routeId: r.id, fromId },
          });
        }
      }
    }
    // The corridor the eye actually sees: one smooth line per run of legs
    // sharing an XTD, drawn at true width by the zoom expression on the layer,
    // with round joins so it flows around waypoints instead of leaving bow-tie
    // corners (fb-msmbbi3g-c). The per-leg quads above stay for hit-testing
    // and editing; this is the everyday look.
    let runCoords: [number, number][] = [];
    let runXtd: number | null = null;
    const flushRun = () => {
      if (runCoords.length >= 2 && runXtd != null && runXtd > 0) {
        const latMid = runCoords.reduce((t, c) => t + c[1], 0) / runCoords.length;
        features.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: runCoords },
          // w0: the line's width in pixels AT ZOOM ZERO for this latitude, which
          // the exponential zoom curve then doubles per level, keeping the
          // corridor at its true width in metres at every zoom.
          properties: { kind: 'xtd-run', w0: xtdRunW0(runXtd, latMid) },
        });
      }
      runCoords = [];
      runXtd = null;
    };
    for (let i = 0; i < r.waypoints.length - 1; i++) {
      const xtd = effectiveLegXtd(r, r.waypoints[i].id);
      if (xtd == null || xtd <= 0) {
        flushRun();
        continue;
      }
      // A leg with a different XTD starts a fresh run: two widths cannot share
      // one stroke, and the seam lands exactly where the width honestly changes.
      if (runXtd != null && xtd !== runXtd) flushRun();
      if (!runCoords.length) {
        runCoords.push(r.waypoints[i].position);
        runXtd = xtd;
      }
      runCoords.push(r.waypoints[i + 1].position);
    }
    flushRun();
  }

  // Active-leg drawing while following: the leg we're steering highlighted bold
  // on the route line, plus a course line from the boat to the waypoint we're
  // making for.
  if (follow) {
    const r = routes.find((rr) => rr.id === follow.routeId);
    if (r && r.waypoints.length >= 1) {
      let target: [number, number];
      let targetName = '';
      if (r.waypoints.length >= 2) {
        // Prefer the stored active leg (activated follow); fall back to the
        // nearest-leg pick for a legacy follow with no stored index.
        const idx =
          follow.legIndex != null
            ? clampFollowLegIndex(r, follow.legIndex)
            : (monitorRoute(r, follow.boat, follow.fromLegIndex ?? 0).activeLegIndex ?? 0);
        if (idx === APPROACH_LEG_INDEX) {
          // Approaching WP1 from wherever we are. The course line below IS the leg,
          // so there's no segment of the route to highlight yet.
          target = r.waypoints[0].position;
          targetName = waypointLabel(r.waypoints[0].name, 0);
        } else {
          const tw = r.waypoints[idx + 1] ?? r.waypoints[r.waypoints.length - 1];
          const twIndex = r.waypoints[idx + 1] ? idx + 1 : r.waypoints.length - 1;
          target = tw.position;
          targetName = waypointLabel(tw.name, twIndex);
          // Highlight the active leg SEGMENT itself, so it's obvious which leg we're
          // steering even before reading the course line. Drawn in the reserved
          // magenta the route colours are kept clear of.
          const legStart = r.waypoints[idx];
          const legEnd = r.waypoints[idx + 1];
          if (legStart && legEnd) {
            features.push({
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: [legStart.position, legEnd.position] },
              properties: { kind: 'active-leg-seg', routeId: r.id, legIndex: idx },
            });
          }
        }
      } else {
        target = r.waypoints[0].position;
        targetName = waypointLabel(r.waypoints[0].name, 0);
      }
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [follow.boat, target] },
        // `name` lets a tap on the active-leg line say where it is heading.
        properties: { kind: 'active-leg', name: targetName },
      });
    }
  }
  for (const r of routes) {
    if (r.waypoints.length < 2) {
      r.waypoints.forEach((w, i) => {
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: w.position },
          properties: { kind: 'waypoint', routeId: r.id, active: r.id === activeRouteId, name: waypointLabel(w.name, i), wpId: w.id, icon: w.icon ?? 'plot-waypoint' },
        });
      });
      continue;
    }
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: r.waypoints.map((w) => w.position) },
      // Carry the route's own colour through onto its line feature. Left off when
      // unset so the layer's coalesce falls back to the default route colour.
      properties: {
        kind: 'route',
        routeId: r.id,
        active: r.id === activeRouteId,
        ...(r.color ? { color: r.color } : {}),
      },
    });
    r.waypoints.forEach((w, i) => {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: w.position },
        properties: { kind: 'waypoint', routeId: r.id, active: r.id === activeRouteId, name: waypointLabel(w.name, i), wpId: w.id, icon: w.icon ?? 'plot-waypoint' },
      });
    });
    const s = summariseRoute(r);
    s.legs.forEach((leg, i) => {
      const from = r.waypoints[i].position;
      const to = r.waypoints[i + 1].position;
      const mid: [number, number] = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2];
      labels.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: mid },
        properties: { text: describeLeg(leg, i), routeId: r.id },
      });
    });
  }
  return {
    features: { type: 'FeatureCollection', features },
    labels: { type: 'FeatureCollection', features: labels },
  };
}

export function ensureRouteLayer(
  map: MaplibreMap,
  routes: Route[],
  activeRouteId: string | null,
  palette: Palette,
  follow?: FollowInfo | null,
  editXtd?: EditXtd | null,
  hazards?: RouteHazardHighlight | null,
): void {
  const p = getPalette(palette);
  const { features, labels } = buildRouteFeatures(routes, activeRouteId, follow, editXtd, hazards);

  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data: features });
  } else {
    (map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(features);
  }
  if (!map.getSource(LABEL_SOURCE_ID)) {
    map.addSource(LABEL_SOURCE_ID, { type: 'geojson', data: labels });
  } else {
    (map.getSource(LABEL_SOURCE_ID) as maplibregl.GeoJSONSource).setData(labels);
  }

  if (!map.getLayer('route-xtd-band')) {
    map.addLayer({
      id: 'route-xtd-band',
      type: 'fill',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'xtd-band'],
      paint: {
        // Green: the corridor is the SAFE water, and it should read as such
        // (fb-msmbbi3g-c). Invisible unless editing, but always rendered,
        // because this quad is what the drag hit-test grabs; the smooth
        // xtd-run line below is what the eye sees day-to-day.
        'fill-color': XTD_GREEN,
        'fill-opacity': ['case', ['get', 'editing'], 0.16, 0],
      },
    });
  }
  if (!map.getLayer('route-xtd-run')) {
    map.addLayer({
      id: 'route-xtd-run',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'xtd-run'],
      layout: {
        // Round joins are the corner smoothing; butt caps stop the corridor
        // poking past the first and last waypoint, where the arrival ring
        // takes over.
        'line-join': 'round',
        'line-cap': 'butt',
      },
      paint: {
        'line-color': XTD_GREEN,
        'line-opacity': 0.14,
        'line-width': ['interpolate', ['exponential', 2], ['zoom'], 0, ['get', 'w0'], 24, ['*', ['get', 'w0'], 16777216]],
      },
    });
  }
  if (!map.getLayer('route-xtd-edge')) {
    map.addLayer({
      id: 'route-xtd-edge',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'xtd-band'],
      paint: {
        'line-color': XTD_GREEN,
        'line-width': ['case', ['get', 'editing'], 1.6, 0.8],
        // The edges only speak while editing; the smooth run carries the look
        // the rest of the time.
        'line-opacity': ['case', ['get', 'editing'], 0.9, 0],
      },
    });
  }
  if (!map.getLayer('route-wp-ring')) {
    map.addLayer({
      id: 'route-wp-ring',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'wp-ring'],
      paint: { 'line-color': p.CHMGD, 'line-width': 1.2, 'line-dasharray': [3, 2], 'line-opacity': 0.85 },
    });
  }
  if (!map.getLayer('route-line')) {
    map.addLayer({
      id: 'route-line',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'route'],
      paint: {
        // Each route draws in its own colour when set, falling back to the app's
        // default route accent when it hasn't got one.
        'line-color': ['coalesce', ['get', 'color'], p.UI_ACCENT],
        'line-width': ['case', ['get', 'active'], 3, 1.6],
        'line-opacity': ['case', ['get', 'active'], 1, 0.6],
      },
    });
  }
  if (!map.getLayer('route-hazard-leg')) {
    map.addLayer({
      id: 'route-hazard-leg',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'hazard-leg'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        // A broad dashed casing under the route line, coloured by severity so each
        // reads distinctly: red for a confirmed hazard (land, or too shallow even
        // at high water), teal for a tidal gate (shallow but passable on the tide),
        // orange for a leg needing a manual tide calc, amber for a plain caution
        // (unknown depth / no coverage). Kept off the magenta the active leg uses
        // so the two never blur together.
        'line-color': [
          'match',
          ['get', 'severity'],
          'hazard',
          '#e53935',
          'gate',
          '#26a69a',
          'manual',
          '#fb8c00',
          '#fdd835',
        ],
        'line-width': 7,
        'line-opacity': 0.6,
        'line-dasharray': [1.5, 1],
      },
    });
  }
  if (!map.getLayer('route-active-leg-seg')) {
    map.addLayer({
      id: 'route-active-leg-seg',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'active-leg-seg'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        // A broad magenta casing laid over the active leg of the route line, so
        // the leg we're steering stands out from the rest of the route without
        // hiding the thin course line drawn on top of it.
        'line-color': p.CHMGD,
        'line-width': 6,
        'line-opacity': 0.5,
      },
    });
  }
  if (!map.getLayer('route-active-leg')) {
    map.addLayer({
      id: 'route-active-leg',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'active-leg'],
      paint: {
        // Bold magenta course line to the waypoint we are making for.
        'line-color': p.CHMGD,
        'line-width': 2.6,
      },
    });
  }
  if (!map.getLayer('route-waypoints')) {
    map.addLayer({
      id: 'route-waypoints',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'waypoint'],
      layout: {
        // Per-waypoint marker: the icon chosen in the waypoint editor, falling
        // back to the square paper-chart waypoint symbol. All ids registered in
        // icons.ts; the property is always set in build() so there's no empty id.
        'icon-image': ['get', 'icon'],
        'icon-size': 0.85,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });
  }
  if (!map.getLayer('route-xtd-handle')) {
    map.addLayer({
      id: 'route-xtd-handle',
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'xtd-handle'],
      paint: {
        'circle-radius': 9,
        'circle-color': p.CHMGD,
        'circle-stroke-color': p.UI_BG,
        'circle-stroke-width': 2,
      },
    });
  }
  if (!map.getLayer('route-waypoint-labels')) {
    map.addLayer({
      id: 'route-waypoint-labels',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'waypoint'],
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Open Sans Regular'],
        'text-size': 11,
        'text-offset': [0.9, 0],
        'text-anchor': 'left',
        'text-optional': true,
      },
      paint: { 'text-color': p.UI_ACCENT, 'text-halo-color': p.UI_BG, 'text-halo-width': 1.4 },
    });
  }
  if (!map.getLayer('route-leg-labels')) {
    map.addLayer({
      id: 'route-leg-labels',
      type: 'symbol',
      source: LABEL_SOURCE_ID,
      layout: {
        'text-field': ['get', 'text'],
        'text-font': ['Open Sans Regular'],
        'text-size': 10,
        'text-offset': [0, -0.8],
        'text-anchor': 'bottom',
        'text-optional': true,
      },
      paint: { 'text-color': p.UI_FG, 'text-halo-color': p.UI_BG, 'text-halo-width': 1.4 },
    });
  }

  // Whether we just added the layers or they already existed (created early by a
  // placed waypoint or a played route, before the async chart cells loaded), keep
  // them above the cells so nothing hides behind the vector chart fills.
  raiseRouteLayers(map);
}

export function updateRouteLayer(
  map: MaplibreMap,
  routes: Route[],
  activeRouteId: string | null,
  follow?: FollowInfo | null,
  editXtd?: EditXtd | null,
  hazards?: RouteHazardHighlight | null,
): void {
  const { features, labels } = buildRouteFeatures(routes, activeRouteId, follow, editXtd, hazards);
  const src = map.getSource(SOURCE_ID);
  const lsrc = map.getSource(LABEL_SOURCE_ID);
  if (src) (src as maplibregl.GeoJSONSource).setData(features);
  if (lsrc) (lsrc as maplibregl.GeoJSONSource).setData(labels);
}
